import { Entity } from './entity.js';
import { pickMinionAggroTarget } from './minionAggroPriority.js';

export class Minion extends Entity {
  constructor({ id, team = 'blue', minionType = 'melee', x = 0, y = 0, z = 0, waypoints = [], mainTurretTarget = null, hp, maxHp }) {
    const stats = {
      melee: { hp: 477, ad: 12, range: 110, ms: 325, radius: 16, bounty: 21, exp: 59, as: 0.658, armor: 0, mr: 0 },
      ranged: { hp: 296, ad: 24, range: 550, ms: 325, radius: 14, bounty: 14, exp: 29, as: 0.625, armor: 0, mr: 0 },
      cannon: { hp: 912, ad: 41, range: 550, ms: 325, radius: 18, bounty: 60, exp: 92, as: 0.625, armor: 0, mr: 0 },
      super: { hp: 1500, ad: 190, range: 110, ms: 325, radius: 20, bounty: 90, exp: 97, as: 0.75, armor: 100, mr: -30 }
    }[minionType] || { hp: 477, ad: 12, range: 110, ms: 325, radius: 16, bounty: 21, exp: 59, as: 0.658, armor: 0, mr: 0 };

    super({
      id,
      name: `Minion (${minionType})`,
      type: `minion_${minionType}`,
      team,
      x,
      y,
      z,
      radius: stats.radius,
      ms: stats.ms,
      attackRange: stats.range,
      as: stats.as,
      ad: stats.ad,
      armor: stats.armor,
      mr: stats.mr,
      hp: hp !== undefined ? hp : stats.hp,
      maxHp: maxHp !== undefined ? maxHp : stats.hp,
      bounty: stats.bounty,
      exp: stats.exp
    });

    this.minionType = minionType;
    this.isSuperMinion = minionType === 'super';
    this.bounty = stats.bounty;
    this.exp = stats.exp;
    this.waypoints = waypoints;
    this.currentWaypointIndex = 0;
    this.mainTurretTarget = mainTurretTarget;
    this.aggroRange = 450; // Radio de detección de enemigos en carril (4.5 u)

    // Apuntar inmediatamente al primer destino de navegación
    const firstTarget = (waypoints && waypoints.length > 0) ? waypoints[0] : mainTurretTarget;
    if (firstTarget) {
      this.targetX = firstTarget.x;
      this.targetZ = firstTarget.z;
      this.facingAngle = Math.atan2(firstTarget.x - x, firstTarget.z - z);
      this.isMoving = true;
    }
  }

  // Búsqueda y Selección de Objetivos de Combate (Prioridad: Enemigo Cercano en Rango > Torreta Objetivo)
  updateAggro(potentialTargets = []) {
    if (this.isDead || this.isStunned) return null;

    // 1. Si ya tiene un objetivo de ataque vivo en rango de combate, mantener foco
    if (this.attackTarget && !this.attackTarget.isDead) {
      const dist = Math.hypot(this.attackTarget.x - this.x, this.attackTarget.z - this.z);
      if (dist <= (this.aggroRange / 100) + 2.0) {
        return this.attackTarget;
      }
    }

    // 2. Buscar unidades enemigas (prioridad champ > minion; FID-MIN-05)
    const enemies = potentialTargets.filter(t => t && !t.isDead && t.team !== this.team && t !== this);
    const inAggro = enemies.filter((enemy) => {
      const dist = Math.hypot(enemy.x - this.x, enemy.z - this.z);
      const aggroLimit = (this.aggroRange / 100) + (enemy.radius / 16);
      return dist <= aggroLimit;
    });
    const bestTarget = pickMinionAggroTarget(this, inAggro);

    if (bestTarget) {
      this.issueAttackCommand(bestTarget);
    } else {
      // 3. Si no hay unidades enemigas en rango, su objetivo principal es la torreta enemiga
      if (this.mainTurretTarget && !this.mainTurretTarget.isDead) {
        const distToTurret = Math.hypot(this.mainTurretTarget.x - this.x, this.mainTurretTarget.z - this.z);
        if (distToTurret <= (this.aggroRange / 100) + (this.mainTurretTarget.radius / 16)) {
          this.issueAttackCommand(this.mainTurretTarget);
        } else {
          this.attackTarget = null;
        }
      } else {
        this.attackTarget = null;
      }
    }

    return this.attackTarget;
  }

  // Navegación Directa a la Torreta Objetivo o Waypoints del Carril
  followWaypoints(dt, obstacleEntities = [], mapBounds = { minX: -14, maxX: 14, minZ: -14, maxZ: 14 }) {
    if (this.isDead || this.isStunned || this.isAttacking) return false;

    let destination = null;

    if (this.waypoints && this.waypoints.length > 0 && this.currentWaypointIndex < this.waypoints.length) {
      destination = this.waypoints[this.currentWaypointIndex];
      const distToWp = Math.hypot(destination.x - this.x, destination.z - this.z);
      if (distToWp <= 0.8) {
        this.currentWaypointIndex++;
        if (this.currentWaypointIndex < this.waypoints.length) {
          destination = this.waypoints[this.currentWaypointIndex];
        } else {
          destination = this.mainTurretTarget;
        }
      }
    } else if (this.mainTurretTarget && !this.mainTurretTarget.isDead) {
      destination = this.mainTurretTarget;
    }

    if (!destination) return false;

    this.targetX = destination.x;
    this.targetZ = destination.z;

    // Separación Suave Exclusiva entre SÚBDITOS Aliados (Excluye torretas y campeones)
    const alliedMinions = obstacleEntities.filter(o => o && !o.isDead && o.team === this.team && o !== this && o.type && o.type.startsWith('minion'));
    alliedMinions.forEach(other => {
      const dx = this.x - other.x;
      const dz = this.z - other.z;
      const d = Math.hypot(dx, dz);
      const minSeparation = (this.radius + other.radius) / 16;
      if (d > 0.0001 && d < minSeparation) {
        const pushForce = (minSeparation - d) * 0.3;
        this.x += (dx / d) * pushForce;
        this.z += (dz / d) * pushForce;
      }
    });

    // Colisión Rígida Infranqueable Exclusivamente con Enemigos
    const enemyObstacles = obstacleEntities.filter(o => o && !o.isDead && o.team !== this.team);

    return this.updatePosition(dt, enemyObstacles, mapBounds);
  }
}
